import { useState, useCallback, useRef } from "react";
import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";

interface ResizableTableHeadProps {
  children: React.ReactNode;
  className?: string;
  initialWidth?: number;
  minWidth?: number;
}

export function ResizableTableHead({
  children,
  className,
  initialWidth = 120,
  minWidth = 50,
}: ResizableTableHeadProps) {
  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const startResize = useCallback(
    (clientX: number) => {
      startX.current = clientX;
      startWidth.current = width;
      setIsResizing(true);
    },
    [width]
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      startResize(e.clientX);

      const onMouseMove = (ev: MouseEvent) => {
        const delta = ev.clientX - startX.current;
        setWidth(Math.max(minWidth, startWidth.current + delta));
      };
      const onMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [startResize, minWidth]
  );

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.stopPropagation();
      const touch = e.touches[0];
      startResize(touch.clientX);

      const onTouchMove = (ev: TouchEvent) => {
        const t = ev.touches[0];
        if (t) {
          const delta = t.clientX - startX.current;
          setWidth(Math.max(minWidth, startWidth.current + delta));
        }
      };
      const onTouchEnd = () => {
        setIsResizing(false);
        document.removeEventListener("touchmove", onTouchMove);
        document.removeEventListener("touchend", onTouchEnd);
      };
      document.addEventListener("touchmove", onTouchMove, { passive: false });
      document.addEventListener("touchend", onTouchEnd);
    },
    [startResize, minWidth]
  );

  return (
    <TableHead
      className={cn("relative select-none border-r border-border/40", className)}
      style={{ width: `${width}px`, minWidth: `${minWidth}px` }}
    >
      {children}
      {/* Hit area: wide invisible zone for easy grabbing */}
      <div
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        className="absolute right-[-12px] top-0 bottom-0 w-[26px] cursor-col-resize z-10 group"
        style={{ touchAction: "none" }}
      >
        {/* Visible handle line */}
        <div
          className={cn(
            "absolute left-1/2 -translate-x-1/2 top-[20%] bottom-[20%] w-[2px] rounded-full transition-all duration-150",
            isResizing
              ? "bg-primary w-[3px] top-[10%] bottom-[10%]"
              : "bg-transparent group-hover:bg-primary/50 group-hover:w-[3px]"
          )}
        />
      </div>
    </TableHead>
  );
}